import { useEffect, useState } from 'react';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import { AUDITOR_ROLE, TENANT_ROLES } from '../../../../shared/solution.mjs';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';
import { GUARDIAN_ROLE, ROLE_LABELS, isStaffEmail } from '../../lib/people-rows.js';
import { AUDITOR_DEFAULT_DAYS, GUARDIAN_DIRECTORY_HINT, RoleChoices, assignableIds, auditorExpiresAt, validAuditorDays } from './people-format.jsx';

const failureText = (err) => (typeof err?.message === 'string' && err.message.trim() !== '' ? err.message : 'That did not work. Try again.');

/** The error of a failed dialog action, shown inside the open dialog (the shared bad Notice of components/Dialog.jsx). */
export { DialogError };

/**
 * Invite or pre-assign. It offers exactly what Manage roles offers (assignableIds, one shared RoleChoices):
 * the portal roles, with the Auditor expiry, and Guardian. `onInvite(email, roles, staff, expiresAt, guardian)`
 * receives the portal roles only; Guardian travels as the last argument (true) because it is stored in the
 * Watch settings, not on the IdP, and is added through the same Watch route Manage roles uses.
 */
export function InviteDialog({ onClose, onInvite, prefillEmail = '', returnFocusRef = null, auditorSelectable = true, viewerSelectable = true, canGuardian = false, guardianCount = 0, maxGuardians = 10, initialRoles = [] }) {
  const [email, setEmail] = useState(prefillEmail);
  const [selected, setSelected] = useState(() => new Set(initialRoles));
  const [auditorDays, setAuditorDays] = useState(AUDITOR_DEFAULT_DAYS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setEmail(prefillEmail); }, [prefillEmail]);
  const staff = isStaffEmail(email.trim());
  const chosen = TENANT_ROLES.filter((r) => selected.has(r));
  const guardian = canGuardian && selected.has(GUARDIAN_ROLE);
  const guardianFull = canGuardian && guardianCount >= maxGuardians;
  const toggle = (id, on) => setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const auditor = chosen.includes(AUDITOR_ROLE);
  const guardianOnly = guardian && chosen.length === 0;
  const ready = email.trim() !== '' && (chosen.length > 0 || guardian) && (!auditor || validAuditorDays(auditorDays));
  // Unsaved while the address or the role choice differs from how the dialog opened. A successful invite closes (unmounts) it.
  useUnsavedChanges(email.trim() !== prefillEmail.trim() || selected.size !== initialRoles.length || initialRoles.some((r) => !selected.has(r)));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    let done = false;
    try {
      // The expiry travels only with the Auditor role; the server refuses an auditor grant without a valid one.
      const expiry = auditor ? auditorExpiresAt(auditorDays) : undefined;
      done = guardian ? await onInvite(email.trim(), chosen, staff, expiry, true)
        : auditor ? await onInvite(email.trim(), chosen, staff, expiry) : await onInvite(email.trim(), chosen, staff);
    } catch (err) {
      setError(failureText(err));
    } finally {
      setBusy(false);
    }
    if (done) onClose();
  }

  return (
    <Dialog
      open
      title="Invite or pre-assign"
      onClose={onClose}
      returnFocusRef={returnFocusRef}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form="invite-form" className="btn-primary" disabled={busy || !ready}>
            {guardianOnly ? (busy ? 'Adding...' : 'Add guardian') : staff ? (busy ? 'Pre-assigning...' : 'Pre-assign roles') : (busy ? 'Inviting...' : 'Invite')}
          </button>
        </>
      )}
    >
      <form id="invite-form" onSubmit={submit} className="space-y-3" aria-label="Invite a member">
        <label className="block text-sm">
          <span className="label">Email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@customer-domain.com" className="input" />
        </label>
        <RoleChoices
          idPrefix="invite"
          ids={assignableIds({ auditorSelectable, viewerSelectable, canGuardian })}
          selected={selected}
          onToggle={toggle}
          auditorDays={auditorDays}
          onAuditorDays={setAuditorDays}
          guardianDisabled={guardianFull}
          guardianNote={guardianFull ? `At most ${maxGuardians} guardians. Remove one first.` : null}
          watchNotes={guardian ? <p className="text-xs text-ipc-muted">{GUARDIAN_DIRECTORY_HINT}</p> : null}
        />
        {staff ? (
          <p className="text-xs text-ipc-muted">IPC Global staff sign in with IPC Global SSO. Assign the roles now; they apply at their first sign-in. No email is sent.</p>
        ) : guardianOnly ? (
          <p className="text-xs text-ipc-muted">Guardian alone sends no invitation and gives no portal access. The person receives Watch notices once the collector finds them in your directory.</p>
        ) : chosen.length > 0 && chosen.every((r) => r === AUDITOR_ROLE) ? (
          <p className="text-xs text-ipc-muted">An Auditor may use an address in any domain, once the customer&apos;s Microsoft Entra admin consent is confirmed. The invitation email comes from the IPC Global identity platform.</p>
        ) : (
          <p className="text-xs text-ipc-muted">Only addresses in a verified domain of the customer&apos;s Microsoft Entra tenant, once its admin consent is confirmed. The invitation email comes from the IPC Global identity platform.</p>
        )}
      </form>
      <DialogError message={error} />
    </Dialog>
  );
}

export function RevokeDialog({ row, onClose, onConfirm, returnFocusRef = null }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function confirm() {
    setBusy(true);
    setError('');
    let done = false;
    try {
      done = await onConfirm(row.email);
    } catch (err) {
      setError(failureText(err));
    } finally {
      setBusy(false);
    }
    if (done) onClose();
  }
  return (
    <Dialog
      open
      title="Revoke access"
      onClose={onClose}
      returnFocusRef={returnFocusRef}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-danger" disabled={busy} onClick={confirm}>{busy ? 'Revoking...' : `Revoke ${row.email}`}</button>
        </>
      )}
    >
      <p>{row.email} will lose access to this customer. An open session ends when its sign-in token expires.</p>
      {row.guardian ? <p>Their Guardian role is not changed. Remove it in Manage roles if they should stop receiving Watch notices.</p> : null}
      <DialogError message={error} />
    </Dialog>
  );
}

export function AddMemberDialog({ role, candidates, onClose, onAddTenant, onAddGuardian, returnFocusRef = null }) {
  const [pick, setPick] = useState('');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const guardian = role === GUARDIAN_ROLE;
  useUnsavedChanges(guardian ? address.trim() !== '' : pick !== '');

  async function add() {
    setBusy(true);
    setError('');
    let done = false;
    try {
      done = guardian ? await onAddGuardian(address.trim()) : await onAddTenant(candidates.find((m) => m.email === pick), role);
    } catch (err) {
      setError(failureText(err));
    } finally {
      setBusy(false);
    }
    if (done) onClose();
  }

  return (
    <Dialog
      open
      title={`Add a member to ${ROLE_LABELS[role] ?? role}`}
      onClose={onClose}
      returnFocusRef={returnFocusRef}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" disabled={busy || (guardian ? address.trim() === '' : pick === '')} onClick={add}>{guardian ? 'Add guardian' : 'Add'}</button>
        </>
      )}
    >
      {guardian ? (
        <div className="text-sm">
          <label htmlFor="add-guardian-address" className="label">Directory address</label>
          <input id="add-guardian-address" type="email" className="input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="name@customer-domain.com" aria-describedby="add-guardian-hint" />
          <p id="add-guardian-hint" className="mt-1 text-xs text-ipc-muted">The collector checks this address in your directory within 15 minutes.</p>
        </div>
      ) : candidates.length === 0 ? (
        <p className="text-ipc-ink">Everyone already has this role.</p>
      ) : (
        <label className="block text-sm">
          <span className="label">Member</span>
          <select className="input" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">Choose a member</option>
            {candidates.map((m) => <option key={m.email} value={m.email}>{m.email}</option>)}
          </select>
        </label>
      )}
      <DialogError message={error} />
    </Dialog>
  );
}
